import type { AssistantExecutionContext, AssistantExecutionResult } from './index';

const LOCATION_SEGMENTS = ['Desktop', 'Documents', 'Downloads', 'Pictures', 'Music', 'Videos'] as const;
type AssistantLocation = 'desktop' | 'documents' | 'downloads' | 'pictures' | 'music' | 'videos' | 'absolute';

const sanitizePathSegment = (value: unknown) =>
  String(value ?? '')
    .replace(/[<>:"/\\|?*\x00-\x1F]+/g, '')
    .trim();

const normalizeWindowsPath = (value: unknown) =>
  String(value ?? '')
    .replace(/\//g, '\\')
    .replace(/\\+/g, '\\')
    .trim();

const ensureSafeAbsolutePath = (value: unknown) => {
  const normalized = normalizeWindowsPath(value);
  if (!normalized) {
    throw new Error('A valid path is required.');
  }

  if (normalized.includes('..')) {
    throw new Error('Parent-directory segments are not allowed in assistant paths.');
  }

  if (!/^[A-Za-z]:\\/.test(normalized) && normalized !== 'C:\\') {
    throw new Error('Assistant paths must be absolute Windows-style paths.');
  }

  return normalized;
};

const joinWindowsPath = (basePath: string, segment: string) => {
  const normalizedBase = ensureSafeAbsolutePath(basePath);
  const trimmedSegment = sanitizePathSegment(segment);
  if (!trimmedSegment) {
    throw new Error('A valid name is required.');
  }

  return normalizedBase.endsWith('\\')
    ? `${normalizedBase}${trimmedSegment}`
    : `${normalizedBase}\\${trimmedSegment}`;
};

const resolveLocationPath = async (
  location: AssistantLocation | undefined,
  path: unknown,
  resolveDocumentsPath: AssistantExecutionContext['resolveDocumentsPath'],
) => {
  if (location === 'absolute') {
    return ensureSafeAbsolutePath(path);
  }

  if (typeof path === 'string' && path.trim()) {
    return ensureSafeAbsolutePath(path);
  }

  const documentsPath = await resolveDocumentsPath();
  const userRoot = documentsPath.replace(/\\Documents$/i, '');

  switch (location) {
    case 'desktop':
      return `${userRoot}\\Desktop`;
    case 'downloads':
      return `${userRoot}\\Downloads`;
    case 'pictures':
      return `${userRoot}\\Pictures`;
    case 'music':
      return `${userRoot}\\Music`;
    case 'videos':
      return `${userRoot}\\Videos`;
    case 'documents':
    default:
      return documentsPath;
  }
};

const getExplorerTitle = (path: string) => (path === 'C:\\' ? 'My Computer' : 'File Explorer');

export async function executeFileExplorerTool(
  actionId: string,
  args: Record<string, unknown>,
  ctx: AssistantExecutionContext,
): Promise<AssistantExecutionResult> {
  switch (actionId) {
    case 'create_folder': {
      const basePath = await resolveLocationPath(args.location as AssistantLocation | undefined, args.path, ctx.resolveDocumentsPath);
      const targetPath = joinWindowsPath(basePath, String(args.name ?? ''));

      await ctx.ensureDir(basePath);
      if (!ctx.pathExists(targetPath)) {
        const created = await ctx.createFolder(targetPath);
        if (!created) {
          throw new Error(`Could not create the folder at ${targetPath}.`);
        }
      }

      ctx.openApp('file-explorer', getExplorerTitle(basePath), basePath);
      return {
        message: `Created folder at ${targetPath}.`,
        shouldClose: true,
      };
    }

    case 'create_file': {
      const basePath = await resolveLocationPath(args.location as AssistantLocation | undefined, args.path, ctx.resolveDocumentsPath);
      const targetPath = joinWindowsPath(basePath, String(args.name ?? ''));
      const content = typeof args.content === 'string' ? args.content : '';

      await ctx.ensureDir(basePath);
      const created = await ctx.createFile(targetPath, content);
      if (!created) {
        throw new Error(`Could not create the file at ${targetPath}.`);
      }

      ctx.openApp('file-explorer', getExplorerTitle(basePath), basePath);
      return {
        message: `Created file at ${targetPath}.`,
        shouldClose: true,
      };
    }

    case 'open_path': {
      const targetPath = ensureSafeAbsolutePath(args.path);
      if (!ctx.pathExists(targetPath)) {
        throw new Error(`The path ${targetPath} does not exist.`);
      }

      ctx.openApp('file-explorer', getExplorerTitle(targetPath), targetPath);
      return {
        message: `Opened ${targetPath}.`,
        shouldClose: true,
      };
    }

    default:
      throw new Error(`Unsupported File Explorer action: ${actionId}.`);
  }
}